/**
 * Utilidades para analizar problemas comunes en los tickets de mantenimiento
 * Agrupa problemas similares aunque se escriban de manera diferente
 */

/**
 * Normalizar texto: quitar acentos, convertir a minúsculas, eliminar caracteres especiales
 */
export function normalizarTexto(texto) {
  if (!texto || typeof texto !== 'string') return '';
  
  return texto
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Quitar acentos
    .replace(/[^a-z0-9\s]/g, ' ') // Reemplazar caracteres especiales con espacios
    .replace(/\s+/g, ' ') // Múltiples espacios a uno solo
    .trim();
}

/**
 * Diccionario de sinónimos para agrupar términos similares
 * Incluye términos de tecnologías, biomédica y mantenimiento
 */
export function obtenerDiccionarioSinonimos() {
  return {
    // ========== COMPUTADORAS Y HARDWARE ==========
    'computadora': [
      'pc', 'laptop', 'portatil', 'portátil', 'maquina', 'máquina', 'equipo', 'computador', 'ordenador', 
      'notebook', 'desktop', 'escritorio', 'cpu', 'workstation', 'estacion', 'computadora de escritorio',
      'computadora portatil', 'computadora portátil', 'equipo de computo', 'equipo de cómputo', 'servidor',
      'all in one', 'all-in-one', 'aio', 'torre', 'gabinete', 'case', 'minitorre', 'sff', 'tablet'
    ],
    
    // ========== PROBLEMAS DE ENCENDIDO ==========
    'no enciende': [
      'no prende', 'no arranca', 'no inicia', 'no funciona', 'no power', 'sin energia', 'sin energía', 
      'sin corriente', 'no bootea', 'no da señal', 
      'no da imagen', 'pantalla negra', 'pantalla en negro', 'no arranca el sistema', 'no inicia el equipo',
      'no enciende el equipo', 'no prende la computadora', 'sin energia electrica', 'sin energía eléctrica',
      'se queda apagado', 'no responde al encender', 'no da vida', 'muerto', 'no tiene corriente'
    ],
    
    // ========== INTERNET Y RED ==========
    'internet': [
      'red', 'wifi', 'wi-fi', 'conexion', 'conexión', 'red inalambrica', 'red inalámbrica', 'network', 
      'lan', 'wan', 'conectividad', 'internet', 'intranet', 'banda ancha', 'conexion a internet',
      'conexión a internet', 'acceso a internet', 'servicio de internet', 'proveedor de internet', 'isp',
      'router', 'modem', 'switch', 'hub', 'access point', 'ap', 'punto de acceso', 'red cableada'
    ],
    'sin internet': [
      'sin conexion', 'sin conexión', 'sin red', 'sin wifi', 'sin wi-fi', 'sin señal', 'no hay internet', 
      'no hay red', 'perdio conexion', 'perdió conexión', 'se corto internet', 'se cortó internet',
      'no funciona internet', 'no funciona la red', 'sin acceso a internet', 'sin acceso a la red',
      'no hay señal wifi', 'no hay señal wi-fi', 'wifi no funciona', 'wi-fi no funciona', 'red caida',
      'red caída', 'internet caido', 'internet caído', 'conexion intermitente', 'conexión intermitente'
    ],
    
    // ========== PERIFÉRICOS ==========
    'impresora': [
      'printer', 'impresion', 'impresión', 'plotter', 'impresora laser', 'impresora láser', 
      'impresora inyeccion', 'impresora inyección', 'impresora inkjet', 'impresora matricial',
      'impresora de punto', 'impresora multifuncional', 'multifuncional', 'mfp', 'impresora hp',
      'impresora canon', 'impresora epson', 'impresora brother', 'impresora lexmark'
    ],
    'teclado': [
      'keyboard', 'teclas', 'teclado mecanico', 'teclado mecánico', 'teclado numerico', 
      'teclado numérico', 'teclado inalambrico', 'teclado inalámbrico', 'teclado usb', 'teclado wireless'
    ],
    'mouse': [
      'raton', 'ratón', 'puntero', 'mouse inalambrico', 'mouse inalámbrico', 'mouse usb', 
      'mouse wireless', 'trackball', 'touchpad', 'pad', 'alfombrilla', 'mouse pad', 'mousepad'
    ],
    'monitor': [
      'pantalla', 'display', 'screen', 'monitor lcd', 'monitor led', 'monitor crt', 'monitor oled',
      'pantalla lcd', 'pantalla led', 'monitor de computadora', 'monitor de pc', 'pantalla del equipo',
      'display del equipo', 'monitor de escritorio', 'monitor externo', 'pantalla externa'
    ],
    'escaner': [
      'scanner', 'escáner', 'escaner', 'digitalizador', 'escaner de documentos', 'escáner de documentos',
      'escaner de cama plana', 'escáner de cama plana', 'escaner de alimentacion', 'escáner de alimentación',
      'escaner adf', 'escaner automatico', 'escáner automático'
    ],
    'webcam': [
      'camara web', 'cámara web', 'webcam', 'camara del equipo', 'cámara del equipo', 
      'camara frontal', 'cámara frontal', 'video camara', 'videocámara', 'usb camera'
    ],
    'auriculares': [
      'audifonos', 'audífonos', 'headphones', 'auriculares', 'diadema', 'headset', 
      'microfono y audifonos', 'micrófono y audífonos', 'cascos', 'audifonos inalambricos', 'audífonos inalámbricos'
    ],
    
    // ========== SOFTWARE ==========
    'software': [
      'programa', 'aplicacion', 'aplicación', 'app', 'sistema', 'sistema operativo', 'os', 'windows', 
      'office', 'sofware', 'programa de computadora', 'software instalado', 'aplicacion instalada',
      'aplicación instalada', 'programa instalado', 'sistema informatico', 'sistema informático',
      'plataforma', 'suite', 'paquete de software', 'programa informatico', 'programa informático'
    ],
    'no funciona software': [
      'programa no funciona', 'aplicacion no funciona', 'aplicación no funciona', 'app no funciona',
      'software no funciona', 'programa no responde', 'aplicacion no responde', 'aplicación no responde',
      'programa se cuelga', 'aplicacion se cuelga', 'aplicación se cuelga', 'programa da error',
      'aplicacion da error', 'aplicación da error', 'programa no abre', 'aplicacion no abre',
      'aplicación no abre', 'programa no inicia', 'aplicacion no inicia', 'aplicación no inicia'
    ],
    'windows': [
      'windows 10', 'windows 11', 'windows 7', 'windows xp', 'sistema windows', 'sistema operativo windows',
      'windows no funciona', 'windows da error', 'windows no inicia', 'windows no arranca', 'sistema operativo',
      'os windows', 'microsoft windows'
    ],
    'office': [
      'microsoft office', 'office no funciona', 'word no funciona', 'excel no funciona', 'powerpoint no funciona',
      'outlook no funciona', 'office da error', 'word da error', 'excel da error', 'powerpoint da error',
      'paquete office', 'suite office', 'microsoft word', 'microsoft excel', 'microsoft powerpoint'
    ],
    
    // ========== RENDIMIENTO ==========
    'lento': [
      'lentitud', 'tarda', 'demora', 'va lento', 'muy lento', 'muy lenta', 'lentitud', 
      'tarda mucho', 'demora mucho', 'tarda demasiado', 'demora demasiado', 'funciona lento',
      'funciona muy lento', 'trabaja lento', 'trabaja muy lento', 'muy lento al cargar', 'tarda en cargar',
      'demora en cargar', 'tarda en responder', 'demora en responder', 'rendimiento lento', 'performance lento'
    ],
    'se congela': [
      'congelado', 'se bloquea', 'bloqueado', 'no responde', 'trabado', 'se queda trabado', 
      'se queda bloqueado', 'se queda congelado', 'se congela', 'se bloquea completamente',
      'no responde a nada', 'se queda colgado', 'se cuelga', 'deja de responder', 'se traba',
      'se queda trabado', 'se queda pegado', 'se queda congelado', 'se queda bloqueado',
      'se queda sin responder', 'quedarse trabado', 'quedarse bloqueado', 'quedarse congelado'
    ],
    
    // ========== ERRORES ==========
    'error': [
      'falla', 'fallo', 'problema', 'no funciona', 'mal funcionamiento', 'defecto', 'averia', 'avería',
      'error', 'da error', 'presenta error', 'tiene error', 'muestra error', 'sale error',
      'error en el equipo', 'falla en el equipo', 'problema con el equipo', 'defecto en el equipo',
      'mal funcionamiento del equipo', 'averia del equipo', 'avería del equipo'
    ],
    'error sistema': [
      'error en sistema', 'falla sistema', 'problema sistema', 'error windows', 'error del sistema',
      'sistema da error', 'sistema presenta error', 'error del sistema operativo', 'falla del sistema',
      'problema del sistema', 'sistema fallando', 'error en windows', 'falla en windows',
      'problema en windows', 'sistema operativo da error', 'error bsod', 'pantalla azul',
      'pantalla azul de la muerte', 'blue screen', 'error critico', 'error crítico'
    ],
    
    // ========== BIOMÉDICA ==========
    'monitor paciente': [
      'monitor biomedico', 'monitor biomédico', 'monitor signos vitales', 'monitor cardiaco', 'monitor cardíaco', 
      'monitor ecg', 'monitor ekg', 'monitor de cabecera', 'central monitoreo', 'monitor de paciente',
      'monitor de signos vitales', 'monitor multiparametrico', 'monitor multiparamétrico', 'monitor de cabecera',
      'monitor paciente adulto', 'monitor paciente pediatrico', 'monitor paciente pediátrico', 'monitor de cuidados intensivos',
      'monitor de uci', 'monitor de terapia intensiva', 'vital signs monitor', 'patient monitor', 'bedside monitor'
    ],
    'bomba infusion': [
      'bomba infusora', 'bomba de infusion', 'bomba de infusión', 'bomba de suero', 'bomba de medicamento', 
      'infusora', 'syringe pump', 'bomba de infusion continua', 'bomba de infusión continua', 'bomba peristaltica',
      'bomba peristáltica', 'bomba de solucion', 'bomba de solución', 'bomba de liquidos', 'bomba de líquidos',
      'infusion pump', 'iv pump', 'bomba iv', 'bomba intravenosa', 'bomba de medicacion', 'bomba de medicación'
    ],
    'electrocardiografo': [
      'ecg', 'ekg', 'electrocardiografo', 'electrocardiograma', 'maquina ecg', 'máquina ecg',
      'electrocardiografo portatil', 'electrocardiógrafo portátil', 'electrocardiografo de 12 derivaciones',
      'electrocardiógrafo de 12 derivaciones', 'equipo ecg', 'equipo ekg', 'electrocardio', 'ecg portatil',
      'ecg portátil', 'maquina de electrocardiograma', 'máquina de electrocardiograma'
    ],
    'desfibrilador': [
      'defibrilador', 'desfibrilador externo', 'dea', 'aed', 'desfibrilador automatico',
      'desfibrilador automático', 'desfibrilador manual', 'desfibrilador monofasico', 'desfibrilador monofásico',
      'desfibrilador bifasico', 'desfibrilador bifásico', 'desfibrilador externo automatico',
      'desfibrilador externo automático', 'defibrillator', 'aed automatico', 'aed automático'
    ],
    'ventilador': [
      'respirador', 'ventilador mecanico', 'ventilador mecánico', 'ventilacion mecanica', 'ventilación mecánica',
      'ventilador pulmonar', 'ventilador de presion positiva', 'ventilador de presión positiva', 'ventilador invasivo',
      'ventilador no invasivo', 'ventilador de uci', 'ventilador de terapia intensiva', 'ventilador de cuidados intensivos',
      'respirador mecanico', 'respirador mecánico', 'ventilador pediatrico', 'ventilador pediátrico',
      'ventilador neonatal', 'mechanical ventilator', 'respirator', 'ventilador bmv', 'ventilador manual'
    ],
    'bomba jeringa': [
      'bomba jeringa', 'bomba de jeringa', 'syringe pump', 'bomba infusora jeringa', 'bomba de jeringa automatica',
      'bomba de jeringa automática', 'bomba de jeringa continua', 'syringe infusion pump', 'bomba de jeringa programable',
      'bomba jeringa electronica', 'bomba jeringa electrónica', 'bomba de infusion jeringa', 'bomba de infusión jeringa'
    ],
    'oximetro': [
      'oxímetro', 'pulsioximetro', 'pulsioxímetro', 'saturometro', 'saturómetro', 'monitor saturacion', 'monitor saturación',
      'oximetro de pulso', 'oxímetro de pulso', 'oximetro digital', 'oxímetro digital', 'oximetro de dedo',
      'oxímetro de dedo', 'pulsioximetro de dedo', 'pulsioxímetro de dedo', 'monitor de saturacion de oxigeno',
      'monitor de saturación de oxígeno', 'spo2 monitor', 'oximetro portatil', 'oxímetro portátil', 'pulse oximeter'
    ],
    'termometro': [
      'termómetro', 'termometro digital', 'termómetro digital', 'termometro clinico', 'termómetro clínico',
      'termometro infrarrojo', 'termómetro infrarrojo', 'termometro sin contacto', 'termómetro sin contacto',
      'termometro de oido', 'termómetro de oído', 'termometro de frente', 'termómetro de frente',
      'termometro electronico', 'termómetro electrónico', 'digital thermometer', 'termometro digital infrarrojo',
      'termómetro digital infrarrojo', 'termometro laser', 'termómetro láser'
    ],
    'estetoscopio': [
      'estetoscopio', 'fonendoscopio', 'estetoscopio electronico', 'estetoscopio electrónico',
      'estetoscopio digital', 'estetoscopio litmann', 'estetoscopio pediatrico', 'estetoscopio pediátrico',
      'estetoscopio neonatal', 'fonendoscopio electronico', 'fonendoscopio electrónico', 'stethoscope'
    ],
    'tensiometro': [
      'tensiómetro', 'baumanometro', 'baumanómetro', 'medidor presion', 'medidor presión',
      'tensiómetro digital', 'baumanometro digital', 'baumanómetro digital', 'monitor de presion arterial',
      'monitor de presión arterial', 'esfigmomanometro', 'esfigmomanómetro', 'monitor de presion',
      'monitor de presión', 'tensiometro automatico', 'tensiómetro automático', 'blood pressure monitor',
      'tensiometro de brazo', 'tensiómetro de brazo', 'tensiometro de munecca', 'tensiómetro de muñeca'
    ],
    'camilla': [
      'mesa quirurgica', 'mesa quirúrgica', 'mesa operaciones', 'mesa de operaciones',
      'camilla quirurgica', 'camilla quirúrgica', 'mesa operatoria', 'mesa de cirugia', 'mesa de cirugía',
      'camilla de operaciones', 'mesa de anestesia', 'surgical table', 'operating table', 'mesa quirurgica electrica',
      'mesa quirúrgica eléctrica', 'camilla electrica', 'camilla eléctrica', 'mesa quirurgica hidraulica',
      'mesa quirúrgica hidráulica'
    ],
    'esterilizador': [
      'autoclave', 'esterilizacion', 'esterilización', 'esterilizador a vapor',
      'esterilizador de vapor', 'autoclave de vapor', 'esterilizador de alta temperatura',
      'esterilizador de baja temperatura', 'autoclave de gravedad', 'autoclave de prevacio',
      'autoclave de prevacío', 'esterilizador de gas', 'esterilizador de oxido de etileno',
      'esterilizador de óxido de etileno', 'esterilizador por plasma', 'sterilizer', 'autoclave machine'
    ],
    'centrifuga': [
      'centrífuga', 'centrifuga laboratorio', 'centrífuga laboratorio',
      'centrifuga de laboratorio', 'centrífuga de laboratorio', 'centrifuga clinica', 'centrífuga clínica',
      'centrifuga de sangre', 'centrífuga de sangre', 'centrifuga refrigerada', 'centrífuga refrigerada',
      'centrifuga microhematocrito', 'centrífuga microhematocrito', 'centrifuge', 'laboratory centrifuge',
      'centrifuga de alta velocidad', 'centrífuga de alta velocidad', 'centrifuga de baja velocidad',
      'centrífuga de baja velocidad'
    ],
    'microscopio': [
      'microscopio', 'microscopio optico', 'microscopio óptico',
      'microscopio electronico', 'microscopio electrónico', 'microscopio binocular', 'microscopio monocular',
      'microscopio de laboratorio', 'microscopio clinico', 'microscopio clínico', 'microscopio digital',
      'microscopio de campo claro', 'microscopio de campo oscuro', 'microscopio de fluorescencia',
      'microscope', 'laboratory microscope', 'clinical microscope', 'microscopio compuesto',
      'microscopio estereoscopico', 'microscopio estereoscópico'
    ],
    'incubadora': [
      'incubadora neonatal', 'incubadora bebes', 'incubadora bebés',
      'incubadora para bebes', 'incubadora para bebés', 'incubadora neonatal electrica', 'incubadora neonatal eléctrica',
      'incubadora de cuidados intensivos', 'incubadora de uci', 'incubadora transportadora',
      'incubadora de transporte', 'incubadora radiante', 'neonatal incubator', 'baby incubator',
      'incubadora de temperatura', 'incubadora con oxigeno', 'incubadora con oxígeno'
    ],
    'nebulizador': [
      'nebulizador', 'nebulizacion', 'nebulización',
      'nebulizador ultrasonic', 'nebulizador ultrasónico', 'nebulizador de malla', 'nebulizador de compresor',
      'nebulizador portatil', 'nebulizador portátil', 'nebulizador electrico', 'nebulizador eléctrico',
      'nebulizador pediatrico', 'nebulizador pediátrico', 'nebulizer', 'ultrasonic nebulizer',
      'nebulizador para asma', 'nebulizador para respiratorio', 'equipo de nebulizacion', 'equipo de nebulización'
    ],
    'silla ruedas': [
      'silla de ruedas', 'silla ruedas', 'silla movilidad', 'silla discapacitados',
      'silla de ruedas manual', 'silla de ruedas electrica', 'silla de ruedas eléctrica', 'silla de ruedas mecanica',
      'silla de ruedas mecánica', 'silla de ruedas portatil', 'silla de ruedas portátil', 'wheelchair',
      'silla de ruedas plegable', 'silla de ruedas deportiva', 'silla de ruedas pediatrica', 'silla de ruedas pediátrica',
      'silla de ruedas hospitalaria', 'silla movilidad reducida', 'silla para discapacitados'
    ],
    'cama hospitalaria': [
      'cama hospitalaria', 'cama hospital', 'cama electrica', 'cama eléctrica', 'cama mecanica',
      'cama mecánica', 'cama manual', 'cama de hospital', 'cama hospitalaria electrica', 'cama hospitalaria eléctrica',
      'cama hospitalaria articulada', 'cama de cuidados intensivos', 'cama de uci', 'cama de terapia intensiva',
      'hospital bed', 'electric bed', 'medical bed', 'cama con barandales', 'cama con rieles'
    ],
    'mesa de luz': [
      'mesa de luz', 'luz quirurgica', 'luz quirúrgica', 'luz operatoria', 'luz de cirugia', 'luz de cirugía',
      'lampara quirurgica', 'lámpara quirúrgica', 'lampara operatoria', 'lámpara operatoria',
      'surgical light', 'operating room light', 'lampara de cirugia', 'lámpara de cirugía',
      'luz led quirurgica', 'luz led quirúrgica', 'lampara led quirurgica', 'lámpara led quirúrgica'
    ],
    'dimmer': [
      'dimmer', 'regulador de luz', 'controlador de luz', 'dimmer de luz', 'regulador intensidad',
      'controlador intensidad', 'dimmer electrico', 'dimmer eléctrico', 'dimmer led', 'light dimmer'
    ],
    
    // ========== MANTENIMIENTO ==========
    'aire acondicionado': [
      'aire', 'clima', 'climatizacion', 'climatización', 'hvac', 'sistema aire', 
      'sistema climatizacion', 'sistema climatización', 'aire acondicionado', 'aire acondicionado central',
      'sistema de aire acondicionado', 'clima central', 'aire centralizado', 'ac central', 'a/c central',
      'split', 'minisplit', 'mini split', 'aire acondicionado split', 'unidad de aire acondicionado',
      'aire no funciona', 'clima no funciona', 'aire no enciende', 'clima no enciende', 'aire no enfría',
      'clima no enfría', 'aire no prende', 'clima no prende', 'hvac no funciona'
    ],
    'calefaccion': [
      'calefacción', 'calefactor', 'sistema calefaccion', 'sistema calefacción', 'caldera',
      'calefaccion central', 'calefacción central', 'sistema de calefaccion', 'sistema de calefacción',
      'calefactor electrico', 'calefactor eléctrico', 'calefactor de gas', 'caldera de gas',
      'caldera electrica', 'caldera eléctrica', 'radiador', 'calefaccion no funciona',
      'calefacción no funciona', 'calefactor no funciona', 'caldera no funciona', 'no calienta'
    ],
    'iluminacion': [
      'iluminación', 'luminaria', 'lampara', 'lámpara', 'foco', 'bombilla', 'led', 'luces', 
      'sistema iluminacion', 'sistema iluminación', 'lampara fluorescente', 'lámpara fluorescente',
      'lampara led', 'lámpara led', 'foco led', 'bombilla led', 'luminaria led', 'tubo fluorescente',
      'lampara quemada', 'lámpara quemada', 'foco quemado', 'bombilla quemada', 'lampara fundida',
      'lámpara fundida', 'foco fundido', 'bombilla fundida', 'lampara no prende', 'lámpara no prende',
      'foco no prende', 'bombilla no prende', 'luces no funcionan', 'sistema iluminacion no funciona',
      'sistema iluminación no funciona', 'luminaria no funciona'
    ],
    'plomeria': [
      'plomería', 'tuberia', 'tubería', 'cañeria', 'cañería', 'agua', 'drenaje', 'desague', 'desagüe', 
      'fuga agua', 'fuga de agua', 'fuga de agua', 'tubo roto', 'tuberia rota', 'tubería rota',
      'cañeria rota', 'cañería rota', 'tubo dañado', 'tuberia dañada', 'tubería dañada',
      'cañeria dañada', 'cañería dañada', 'fuga en tuberia', 'fuga en tubería', 'fuga en cañeria',
      'fuga en cañería', 'drenaje tapado', 'drenaje bloqueado', 'desague tapado', 'desagüe tapado',
      'desague bloqueado', 'desagüe bloqueado', 'no sale agua', 'no baja el agua', 'agua no fluye',
      'presion de agua', 'presión de agua', 'agua con poca presion', 'agua con poca presión',
      'agua goteando', 'tubo goteando', 'tuberia goteando', 'tubería goteando'
    ],
    'electricidad': [
      'luz', 'corriente', 'electrico', 'eléctrico', 'cableado', 'tablero', 'interruptor', 
      'corto circuito', 'corto-circuito', 'sin luz', 'sin corriente', 'sin energia electrica',
      'sin energía eléctrica', 'falta de luz', 'falta de corriente', 'falta de energia',
      'falta de energía', 'apagones', 'cortes de luz', 'cortes de corriente', 'cortes de energia',
      'cortes de energía', 'tablero electrico', 'tablero eléctrico', 'interruptor no funciona',
      'interruptor roto', 'switch no funciona', 'breaker', 'breaker no funciona', 'breaker saltado',
      'fusible quemado', 'fusible fundido', 'cableado dañado', 'cableado en mal estado',
      'instalacion electrica', 'instalación eléctrica', 'cableado defectuoso', 'corto en cableado',
      'sobrecarga electrica', 'sobrecarga eléctrica', 'voltaje bajo', 'voltaje alto', 'variaciones de voltaje'
    ],
    'pintura': [
      'pintar', 'pintura', 'reparacion pared', 'reparación pared', 'mantenimiento pared',
      'pintar pared', 'pintar muro', 'pintura descascarada', 'pintura desprendida', 'pintura dañada',
      'pintura en mal estado', 'reparar pintura', 'retocar pintura', 'aplicar pintura', 'dar pintura',
      'pintura de pared', 'pintura de muro', 'reparacion de pintura', 'reparación de pintura'
    ],
    'cerradura': [
      'cerrojo', 'candado', 'chapa', 'llave', 'no abre', 'no cierra', 'cerradura rota',
      'cerradura no funciona', 'cerradura dañada', 'cerradura en mal estado', 'chapa rota',
      'chapa no funciona', 'cerrojo roto', 'cerrojo no funciona', 'candado roto', 'candado no abre',
      'llave no funciona', 'llave no abre', 'cerradura trabada', 'cerradura bloqueada',
      'cerradura electronica', 'cerradura electrónica', 'cerradura digital', 'lock', 'lock no funciona',
      'cerradura automatica', 'cerradura automática', 'cerradura de seguridad'
    ],
    'ventana': [
      'ventana rota', 'vidrio roto', 'vidrio quebrado', 'cristal roto', 'persiana', 'cortina',
      'ventana dañada', 'ventana en mal estado', 'vidrio dañado', 'cristal dañado',
      'ventana no cierra', 'ventana no abre', 'ventana trabada', 'ventana bloqueada',
      'persiana rota', 'persiana no funciona', 'cortina rota', 'cortina no funciona',
      'persiana no sube', 'persiana no baja', 'vidrio agrietado', 'cristal agrietado',
      'ventana con fuga', 'ventana con corriente', 'ventana que gotea', 'sellado de ventana'
    ],
    'puerta': [
      'puerta rota', 'bisagra', 'gozne', 'puerta no cierra', 'puerta no abre',
      'puerta dañada', 'puerta en mal estado', 'bisagra rota', 'gozne roto',
      'bisagra suelta', 'gozne suelto', 'puerta trabada', 'puerta bloqueada',
      'puerta desalineada', 'puerta no cierra bien', 'puerta no abre bien',
      'cerradura de puerta', 'pomo de puerta', 'picaporte', 'picaporte roto',
      'picaporte no funciona', 'pomo roto', 'pomo no funciona', 'puerta con holgura',
      'puerta chirria', 'puerta rechina', 'puerta hace ruido'
    ],
    'ascensor': [
      'elevador', 'montacargas', 'ascensor no funciona', 'elevador no funciona',
      'ascensor dañado', 'elevador dañado', 'ascensor en mal estado', 'elevador en mal estado',
      'ascensor trabado', 'elevador trabado', 'ascensor bloqueado', 'elevador bloqueado',
      'ascensor no sube', 'elevador no sube', 'ascensor no baja', 'elevador no baja',
      'ascensor atascado', 'elevador atascado', 'ascensor se quedó', 'elevador se quedó',
      'puerta de ascensor', 'puerta de elevador', 'puerta ascensor no funciona',
      'puerta elevador no funciona', 'ascensor con falla', 'elevador con falla', 'elevator'
    ],
    'escalera': [
      'escaleras', 'escalones', 'barandal', 'pasamanos',
      'escalera dañada', 'escalones dañados', 'escalon roto', 'escalón roto',
      'escalon suelto', 'escalón suelto', 'barandal roto', 'pasamanos roto',
      'barandal suelto', 'pasamanos suelto', 'barandal dañado', 'pasamanos dañado',
      'escalera en mal estado', 'escalones en mal estado', 'escalon faltante', 'escalón faltante',
      'barandal flojo', 'pasamanos flojo', 'reparar escalera', 'reparar escalones'
    ],
    'techo': [
      'cielo raso', 'cielo razo', 'gotera', 'filtracion', 'filtración', 'humedad techo',
      'techo con gotera', 'techo con filtración', 'techo con filtración', 'techo mojado',
      'techo con humedad', 'humedad en techo', 'filtracion en techo', 'filtración en techo',
      'gotera en techo', 'cielo raso dañado', 'cielo raso mojado', 'cielo raso con humedad',
      'plafon dañado', 'plafón dañado', 'plafon con humedad', 'plafón con humedad',
      'techo dañado', 'techo en mal estado', 'reparar techo', 'reparar gotera',
      'arreglar gotera', 'arreglar filtración', 'arreglar filtración'
    ],
    'muro': [
      'pared', 'muro', 'grieta', 'fisura', 'reparacion pared', 'reparación pared',
      'pared dañada', 'muro dañado', 'pared con grieta', 'muro con grieta',
      'pared con fisura', 'muro con fisura', 'grieta en pared', 'fisura en pared',
      'grieta en muro', 'fisura en muro', 'pared agrietada', 'muro agrietado',
      'pared en mal estado', 'muro en mal estado', 'reparar pared', 'reparar muro',
      'arreglar pared', 'arreglar muro', 'pintar pared', 'pintar muro', 'humedad en pared',
      'humedad en muro', 'pared con humedad', 'muro con humedad'
    ],
    'piso': [
      'suelo', 'piso', 'loseta', 'baldosa', 'alfombra', 'pisos',
      'piso dañado', 'suelo dañado', 'piso en mal estado', 'suelo en mal estado',
      'loseta rota', 'baldosa rota', 'loseta suelta', 'baldosa suelta',
      'loseta faltante', 'baldosa faltante', 'alfombra dañada', 'alfombra rota',
      'alfombra desgastada', 'piso agrietado', 'suelo agrietado', 'piso con grieta',
      'suelo con grieta', 'reparar piso', 'reparar suelo', 'arreglar piso', 'arreglar suelo',
      'cambiar loseta', 'cambiar baldosa', 'cambiar alfombra', 'piso mojado', 'suelo mojado'
    ],
    'sanitario': [
      'baño', 'bathroom', 'wc', 'inodoro', 'taza', 'lavabo', 'regadera', 'ducha', 'grifo', 'llave agua',
      'inodoro no funciona', 'taza no funciona', 'inodoro tapado', 'taza tapada', 'inodoro bloqueado',
      'taza bloqueada', 'inodoro no baja', 'taza no baja', 'inodoro no tira', 'taza no tira',
      'lavabo no funciona', 'lavabo tapado', 'lavabo bloqueado', 'lavabo no baja',
      'regadera no funciona', 'ducha no funciona', 'regadera no sale agua', 'ducha no sale agua',
      'grifo no funciona', 'llave no funciona', 'grifo no sale agua', 'llave no sale agua',
      'grifo gotea', 'llave gotea', 'grifo pierde agua', 'llave pierde agua',
      'baño tapado', 'wc tapado', 'baño bloqueado', 'wc bloqueado', 'drenaje de baño'
    ],
    'alarma': [
      'sistema alarma', 'detector humo', 'detector humo', 'extintor', 'sistema seguridad',
      'alarma no funciona', 'sistema alarma no funciona', 'alarma dañada', 'sistema alarma dañado',
      'detector humo no funciona', 'detector de humo no funciona', 'detector humo dañado',
      'detector de humo dañado', 'extintor vencido', 'extintor vacio', 'extintor vacío',
      'extintor no funciona', 'sistema seguridad no funciona', 'alarma disparada', 'alarma activada',
      'alarma suena', 'alarma sin funcionar', 'detector de incendio', 'detector incendio no funciona',
      'sistema contra incendio', 'sistema contra incendios', 'sprinkler', 'rociador', 'rociador no funciona'
    ],
    'gas': [
      'gas lp', 'tanque gas', 'calentador', 'boiler', 'calentador agua',
      'gas no funciona', 'tanque gas vacio', 'tanque gas vacío', 'tanque gas dañado',
      'calentador no funciona', 'boiler no funciona', 'calentador agua no funciona',
      'calentador no prende', 'boiler no prende', 'calentador no calienta', 'boiler no calienta',
      'fuga de gas', 'olor a gas', 'fuga gas', 'tanque con fuga', 'calentador con fuga',
      'boiler con fuga', 'calentador dañado', 'boiler dañado', 'calentador en mal estado',
      'boiler en mal estado', 'calentador de paso', 'boiler de paso', 'calentador instantaneo',
      'calentador instantáneo', 'water heater', 'gas heater'
    ],
    
    // ========== EQUIPOS GENERALES ==========
    'proyector': ['proyector', 'cañon', 'cañón', 'beam', 'video beam'],
    'pantalla': ['pantalla proyeccion', 'pantalla proyección', 'screen', 'pantalla led', 'pantalla lcd'],
    'microfono': ['micrófono', 'microfono', 'mic', 'sistema audio', 'altavoz', 'bocina'],
    'telefono': ['teléfono', 'telefono', 'sistema telefonia', 'sistema telefonía', 'extension', 'extensión'],
    'fotocopiadora': ['copiadora', 'fotocopiadora', 'multifuncional', 'multifunction'],
    'fax': ['fax', 'maquina fax', 'máquina fax'],
    
    // ========== PROBLEMAS ESPECÍFICOS ==========
    'no imprime': [
      'no imprime', 'no esta imprimiendo', 'no está imprimiendo', 'problema impresion', 'problema impresión',
      'impresora no imprime', 'printer no imprime', 'no imprime nada', 'imprime en blanco',
      'impresora da error', 'printer da error', 'error al imprimir', 'falla al imprimir',
      'impresora trabada', 'printer trabada', 'impresora atascada', 'printer atascada',
      'papel atascado', 'papel trabado', 'tinta acabada', 'tinta agotada', 'cartucho vacio',
      'cartucho vacío', 'sin tinta', 'impresora sin tinta', 'toner agotado', 'toner vacio', 'toner vacío'
    ],
    'no conecta': [
      'no conecta', 'no se conecta', 'sin señal', 'sin conexion', 'sin conexión',
      'no conecta a internet', 'no conecta a la red', 'no conecta al wifi', 'no conecta al wi-fi',
      'sin señal de wifi', 'sin señal de wi-fi', 'conexion perdida', 'conexión perdida',
      'no detecta red', 'no encuentra red', 'no se conecta a nada', 'conexion intermitente',
      'conexión intermitente', 'se desconecta', 'se desconecta solo', 'conexion inestable',
      'conexión inestable', 'wifi no conecta', 'wi-fi no conecta', 'red no conecta'
    ],
    'apagado': [
      'se apaga', 'se apaga solo', 'se apaga sola', 'apagado inesperado', 'apaga solo',
      'se apaga constantemente', 'se apaga frecuentemente', 'se apaga sin razon', 'se apaga sin razón',
      'se apaga de repente', 'se apaga de pronto', 'apagado automatico', 'apagado automático',
      'se apaga automaticamente', 'se apaga automáticamente',
      'equipo se apaga', 'computadora se apaga', 'maquina se apaga', 'máquina se apaga',
      'se apaga inesperadamente'
    ],
    'ruido': [
      'hace ruido', 'hace mucho ruido', 'ruido extraño', 'sonido raro', 'zumbido',
      'ruido anormal', 'ruido fuerte', 'ruido molesto', 'ruido excesivo',
      'zumbido constante', 'zumbido molesto', 'sonido extraño', 'sonido anormal', 'sonido raro',
      'ruido de motor', 'ruido de ventilador', 'ruido de disco', 'ruido de equipo',
      'equipo hace ruido', 'maquina hace ruido', 'máquina hace ruido', 'hace ruido extraño',
      'hace ruido raro', 'ruido que no deberia', 'ruido que no debería', 'ruido sospechoso'
    ],
    'sobrecalentamiento': [
      'se calienta mucho', 'muy caliente', 'sobrecalentamiento', 'sobre calentamiento',
      'equipo se calienta', 'computadora se calienta', 'maquina se calienta', 'máquina se calienta',
      'se calienta demasiado', 'muy caliente al tacto', 'calentamiento excesivo',
      'temperatura alta', 'temperatura elevada', 'se sobrecalienta', 'calentamiento anormal',
      'ventilador no funciona', 'ventilador no enfría', 'sistema de enfriamiento', 'cooling system',
      'se calienta y se apaga', 'se apaga por calor', 'overheating', 'temperatura critica', 'temperatura crítica'
    ],
    'vibracion': [
      'vibra', 'vibracion', 'vibración', 'temblor', 'tembla',
      'equipo vibra', 'maquina vibra', 'máquina vibra', 'computadora vibra', 'vibra mucho',
      'vibracion excesiva', 'vibración excesiva', 'vibracion anormal', 'vibración anormal',
      'temblor constante', 'tembla mucho', 'vibra constantemente', 'vibracion molesta',
      'vibración molesta', 'vibra fuerte', 'temblor fuerte', 'vibracion que se siente',
      'vibración que se siente', 'vibra anormalmente', 'temblor anormal'
    ],
    'fuga': [
      'goteo', 'fuga', 'escapes', 'pierde', 'filtracion', 'filtración',
      'gotea agua', 'pierde agua', 'escapes de agua', 'filtracion de agua',
      'filtración de agua', 'gotea constantemente', 'gotea sin parar', 'fuga constante',
      'pierde agua constantemente', 'goteo continuo', 'fuga continua', 'escapes continuos',
      'gotea mucho', 'fuga grande', 'pierde mucha agua', 'fuga visible', 'goteo visible',
      'humedad por fuga', 'humedad por goteo', 'charco por fuga', 'agua acumulada'
    ],
    'oxidacion': [
      'oxido', 'óxido', 'oxidado', 'oxidación', 'herrumbre', 'corrosion', 'corrosión',
      'muy oxidado', 'muy oxidada', 'con oxido', 'con óxido', 'con herrumbre', 'con corrosion',
      'con corrosión', 'oxidacion excesiva', 'oxidación excesiva', 'corrosion avanzada',
      'corrosión avanzada', 'herrumbre visible', 'oxido visible', 'óxido visible',
      'pieza oxidada', 'parte oxidada', 'componente oxidado', 'equipo oxidado', 'maquina oxidada',
      'máquina oxidada', 'superficie oxidada', 'metal oxidado', 'hierro oxidado'
    ],
    'pantalla rota': [
      'pantalla rota', 'pantalla quebrada', 'pantalla dañada', 'pantalla partida', 'pantalla agrietada',
      'pantalla con grieta', 'pantalla con fisura', 'pantalla con rajadura', 'vidrio roto',
      'cristal roto', 'display roto', 'screen roto', 'pantalla lcd rota', 'pantalla led rota',
      'monitor roto', 'monitor quebrado', 'monitor dañado', 'pantalla táctil rota',
      'touch screen roto', 'pantalla tactil rota', 'pantalla táctil rota'
    ],
    'sin audio': [
      'sin audio', 'sin sonido', 'no hay audio', 'no hay sonido', 'no se escucha',
      'sin volumen', 'sin salida de audio', 'audio no funciona', 'sonido no funciona',
      'altavoz no funciona', 'bocina no funciona', 'parlante no funciona', 'speaker no funciona',
      'sin audio en computadora', 'sin sonido en computadora', 'audio apagado', 'sonido apagado',
      'mute', 'silenciado', 'volumen en cero', 'audio deshabilitado', 'sonido deshabilitado'
    ],
    'teclas no funcionan': [
      'teclas no funcionan', 'teclas no responden', 'tecla no funciona', 'tecla no responde',
      'algunas teclas no funcionan', 'algunas teclas no responden', 'teclado algunas teclas',
      'teclas trabadas', 'teclas pegadas', 'teclas que no funcionan', 'teclas muertas',
      'teclado dañado', 'teclado roto', 'teclas faltantes', 'teclas rotas'
    ],
    'bateria no carga': [
      'bateria no carga', 'batería no carga', 'bateria no funciona', 'batería no funciona',
      'bateria descargada', 'batería descargada', 'bateria agotada', 'batería agotada',
      'bateria no dura', 'batería no dura', 'bateria se descarga rapido', 'batería se descarga rápido',
      'bateria defectuosa', 'batería defectuosa', 'cargador no funciona', 'cargador no carga',
      'no carga bateria', 'no carga batería', 'problema con bateria', 'problema con batería'
    ]
  };
}

/**
 * Encontrar el término principal para un texto dado
 */
export function encontrarTerminoPrincipal(textoNormalizado, diccionario) {
  // Primero buscar términos de múltiples palabras (más específicos)
  const terminosMultiPalabra = Object.keys(diccionario).filter(t => t.includes(' '));
  for (const terminoPrincipal of terminosMultiPalabra) {
    if (textoNormalizado.includes(terminoPrincipal)) {
      return terminoPrincipal;
    }
    // Verificar sinónimos de términos multi-palabra
    const sinonimos = diccionario[terminoPrincipal] || [];
    for (const sinonimo of sinonimos) {
      if (textoNormalizado.includes(sinonimo)) {
        return terminoPrincipal;
      }
    }
  }
  
  // Luego buscar términos de una palabra
  for (const [terminoPrincipal, sinonimos] of Object.entries(diccionario)) {
    if (terminoPrincipal.includes(' ')) continue; // Ya se buscaron arriba
    
    // Verificar si el término principal está en el texto (como palabra completa)
    const regex = new RegExp(`\\b${terminoPrincipal}\\b`, 'i');
    if (regex.test(textoNormalizado)) {
      return terminoPrincipal;
    }
    
    // Verificar si algún sinónimo está en el texto (como palabra completa)
    for (const sinonimo of sinonimos) {
      if (sinonimo.includes(' ')) continue; // Solo palabras individuales aquí
      const regexSinonimo = new RegExp(`\\b${sinonimo}\\b`, 'i');
      if (regexSinonimo.test(textoNormalizado)) {
        return terminoPrincipal;
      }
    }
  }
  
  return null;
}

/**
 * Extraer frases clave de una descripción
 */
export function extraerFrasesClave(descripcion) {
  const texto = normalizarTexto(descripcion || '' );
  const frases = [];
  
  // Frases comunes de problemas
  const patronesProblemas = [
    /no\s+enciende/gi,
    /no\s+prende/gi,
    /no\s+arranca/gi,
    /no\s+inicia/gi,
    /no\s+funciona/gi,
    /no\s+esta\s+funcionando/gi,
    /no\s+está\s+funcionando/gi,
    /esta\s+roto/gi,
    /esta\s+rota/gi,
    /esta\s+dana\w*/gi,
    /esta\s+dañado/gi,
    /esta\s+dañada/gi,
    /no\s+imprime/gi,
    /no\s+conecta/gi,
    /no\s+se\s+conecta/gi,
    /sin\s+internet/gi,
    /sin\s+conexion/gi,
    /sin\s+conexión/gi,
    /sin\s+red/gi,
    /muy\s+lento/gi,
    /muy\s+lenta/gi,
    /va\s+lento/gi,
    /va\s+muy\s+lento/gi,
    /se\s+congela/gi,
    /se\s+bloquea/gi,
    /se\s+trabaja/gi,
    /se\s+trabado/gi,
    /no\s+responde/gi,
    /deja\s+de\s+responder/gi,
    /error\s+en/gi,
    /error\s+del/gi,
    /falla\s+en/gi,
    /falla\s+del/gi,
    /se\s+apaga/gi,
    /se\s+apaga\s+solo/gi,
    /hace\s+ruido/gi,
    /hace\s+mucho\s+ruido/gi,
    /gotea/gi,
    /gotea\s+agua/gi,
    /fuga/gi,
    /fuga\s+de\s+agua/gi,
    /no\s+abre/gi,
    /no\s+cierra/gi,
    /no\s+prende\s+la/gi,
    /no\s+enciende\s+la/gi,
    /no\s+arranca\s+el/gi,
    /no\s+funciona\s+el/gi,
    /no\s+funciona\s+la/gi,
    /problema\s+con/gi,
    /problema\s+en/gi,
    /problema\s+del/gi,
    /problema\s+de\s+la/gi,
    /se\s+quedo/gi,
    /se\s+quedó/gi,
    /se\s+calienta/gi,
    /se\s+calienta\s+mucho/gi,
    /muy\s+caliente/gi,
    /vibra/gi,
    /vibra\s+mucho/gi,
    /tembla/gi,
    /oxidado/gi,
    /muy\s+oxidado/gi,
    /con\s+oxido/gi,
    /con\s+óxido/gi,
    /pantalla\s+rota/gi,
    /pantalla\s+quebrada/gi,
    /pantalla\s+dañada/gi,
    /sin\s+audio/gi,
    /sin\s+sonido/gi,
    /no\s+se\s+escucha/gi,
    /teclas\s+no\s+funcionan/gi,
    /tecla\s+no\s+funciona/gi,
    /bateria\s+no\s+carga/gi,
    /batería\s+no\s+carga/gi,
    /bateria\s+no\s+dura/gi,
    /batería\s+no\s+dura/gi,
    /perdio\s+conexion/gi,
    /perdió\s+conexión/gi,
    /se\s+corto/gi,
    /se\s+cortó/gi,
    /tapado/gi,
    /tapada/gi,
    /bloqueado/gi,
    /bloqueada/gi,
    /no\s+baja/gi,
    /no\s+sale/gi,
    /no\s+prende\s+el/gi,
    /no\s+prende\s+la/gi,
    /quemado/gi,
    /quemada/gi,
    /fundido/gi,
    /fundida/gi
  ];
  
  // Buscar patrones
  for (const patron of patronesProblemas) {
    const matches = texto.match(patron);
    if (matches) {
      frases.push(...matches);
    }
  }

  return frases;
}

/**
 * Obtener categoría del problema
 */
export function obtenerCategoria(termino) {
  const categorias = {
    // Hardware
    'computadora': 'Hardware',
    'monitor': 'Hardware',
    'no enciende': 'Hardware',
    'apagado': 'Hardware',
    'sobrecalentamiento': 'Hardware',
    'pantalla rota': 'Hardware',
    'bateria no carga': 'Hardware',
    'ruido': 'Hardware',
    'vibracion': 'Hardware',
    'webcam': 'Hardware',
    'auriculares': 'Hardware',
    
    // Software
    'software': 'Software',
    'no funciona software': 'Software',
    'error sistema': 'Software',
    'windows': 'Software',
    'office': 'Software',
    'error': 'Error',
    
    // Red
    'internet': 'Red',
    'sin internet': 'Red',
    'no conecta': 'Red',
    
    // Periférico
    'impresora': 'Periférico',
    'teclado': 'Periférico',
    'mouse': 'Periférico',
    'escaner': 'Periférico',
    'no imprime': 'Periférico',
    'teclas no funcionan': 'Periférico',
    'sin audio': 'Periférico',
    
    // Rendimiento
    'lento': 'Rendimiento',
    'se congela': 'Rendimiento',
    
    // Biomédica
    'monitor paciente': 'Biomédica',
    'bomba infusion': 'Biomédica',
    'electrocardiografo': 'Biomédica',
    'desfibrilador': 'Biomédica',
    'ventilador': 'Biomédica',
    'bomba jeringa': 'Biomédica',
    'oximetro': 'Biomédica',
    'termometro': 'Biomédica',
    'estetoscopio': 'Biomédica',
    'tensiometro': 'Biomédica',
    'camilla': 'Biomédica',
    'esterilizador': 'Biomédica',
    'centrifuga': 'Biomédica',
    'microscopio': 'Biomédica',
    'incubadora': 'Biomédica',
    'nebulizador': 'Biomédica',
    'silla ruedas': 'Biomédica',
    
    // Mantenimiento
    'aire acondicionado': 'Mantenimiento',
    'calefaccion': 'Mantenimiento',
    'iluminacion': 'Mantenimiento',
    'plomeria': 'Mantenimiento',
    'electricidad': 'Mantenimiento',
    'pintura': 'Mantenimiento',
    'cerradura': 'Mantenimiento',
    'ventana': 'Mantenimiento',
    'puerta': 'Mantenimiento',
    'ascensor': 'Mantenimiento',
    'escalera': 'Mantenimiento',
    'techo': 'Mantenimiento',
    'muro': 'Mantenimiento',
    'piso': 'Mantenimiento',
    'sanitario': 'Mantenimiento',
    'alarma': 'Mantenimiento',
    'gas': 'Mantenimiento',
    'fuga': 'Mantenimiento',
    'oxidacion': 'Mantenimiento',
    
    // Equipos generales
    'proyector': 'Equipos',
    'pantalla': 'Equipos',
    'microfono': 'Equipos',
    'telefono': 'Equipos',
    'fotocopiadora': 'Equipos',
    'fax': 'Equipos',
    
    // Biomédica adicional
    'cama hospitalaria': 'Biomédica',
    'mesa de luz': 'Biomédica',
    'dimmer': 'Biomédica'
  };
  
  return categorias[termino] || 'General';
}

/**
 * Capitalizar primera letra
 */
export function capitalizarPrimeraLetra(texto) {
  if (!texto) return '';
  return texto.charAt(0).toUpperCase() + texto.slice(1);
}

/**
 * Analizar problemas de los tickets
 */
export function analizarProblemas(tickets, filtroMinimo = 2, filtroTopN = 20) {
  if (!tickets || tickets.length === 0) {
    console.warn('No hay tickets para analizar');
    return {
      problemasAnalizados: [],
      problemasRanking: [],
      totalTickets: 0,
      ticketsConProblema: 0
    };
  }

  const diccionario = obtenerDiccionarioSinonimos();
  const problemasMap = new Map();
  const ticketsConProblema = [];

  // Analizar cada ticket
  for (const ticket of tickets) {
    const descripcion = ticket.descripcion || ticket.titulo || '';
    if (!descripcion || descripcion.trim().length < 5) continue;

    const descripcionNormalizada = normalizarTexto(descripcion);
    const terminoPrincipal = encontrarTerminoPrincipal(descripcionNormalizada, diccionario);
    const frasesClave = extraerFrasesClave(descripcion);

    // Crear clave del problema
    let problemaClave = 'otro';
    let problemaNombre = 'Otro problema';
    let categoria = 'General';
    const terminosRelacionados = [];

    if (terminoPrincipal) {
      problemaClave = terminoPrincipal;
      problemaNombre = capitalizarPrimeraLetra(terminoPrincipal);
      categoria = obtenerCategoria(terminoPrincipal);
      
      // Agregar sinónimos encontrados
      const sinonimos = diccionario[terminoPrincipal] || [];
      terminosRelacionados.push(...sinonimos.filter(s => descripcionNormalizada.includes(s)));
    } else if (frasesClave.length > 0) {
      // Usar la primera frase clave como problema
      problemaClave = frasesClave[0];
      problemaNombre = capitalizarPrimeraLetra(frasesClave[0]);
      categoria = 'Problema específico';
    } else {
      // Extraer palabras clave más comunes
      const palabras = descripcionNormalizada.split(/\s+/).filter(p => p.length > 3);
      if (palabras.length > 0) {
        problemaClave = palabras[0];
        problemaNombre = capitalizarPrimeraLetra(palabras[0]);
        categoria = 'General';
      }
    }

    // Agregar o actualizar el problema
    if (!problemasMap.has(problemaClave)) {
      problemasMap.set(problemaClave, {
        nombre: problemaNombre,
        categoria: categoria,
        cantidad: 0,
        terminosRelacionados: [...new Set(terminosRelacionados)],
        ejemplos: [],
        tickets: []
      });
    }

    const problema = problemasMap.get(problemaClave);
    problema.cantidad++;
    problema.tickets.push(ticket);
    
    // Agregar ejemplo (máximo 10)
    if (problema.ejemplos.length < 10) {
      const ejemplo = descripcion.length > 100 ? descripcion.substring(0, 100) + '...' : descripcion;
      if (!problema.ejemplos.includes(ejemplo)) {
        problema.ejemplos.push(ejemplo);
      }
    }

    ticketsConProblema.push(ticket);
  }

  // Convertir a array y filtrar por mínimo
  const problemasAnalizados = Array.from(problemasMap.values())
    .filter(p => p.cantidad >= filtroMinimo)
    .sort((a, b) => b.cantidad - a.cantidad);

  // Aplicar límite de top N
  let problemasRanking;
  if (filtroTopN > 0) {
    problemasRanking = problemasAnalizados.slice(0, filtroTopN);
  } else {
    problemasRanking = problemasAnalizados;
  }

  // Calcular porcentajes
  const totalTickets = tickets.length;
  problemasRanking = problemasRanking.map(problema => ({
    ...problema,
    porcentaje: totalTickets > 0 ? Number(((problema.cantidad / totalTickets) * 100).toFixed(1)) : 0
  }));

  console.log(` Análisis completado: ${problemasRanking.length} problemas identificados`);

  return {
    problemasAnalizados,
    problemasRanking,
    totalTickets,
    ticketsConProblema: ticketsConProblema.length
  };
}

